import React, { forwardRef } from "react";
import SelectBase, {
  SingleValue,
  StylesConfig,
  GroupBase,
} from "react-select";
import CreatableSelect from "react-select/creatable";

interface Option {
  value: string;
  label: string;
}

interface SelectProps {
  options: Option[];
  placeholder?: string;
  onChange: (option: Option | null) => void;
  className?: string;
  value?: Option | string;
  isLoading?: boolean;
  isDisabled?: boolean;
  error?: string;
  isCreatable?: boolean;
  onCreateOption?: (inputValue: string) => void;
  menuPortalTarget?: HTMLElement | null;
}

// Clean sharp-stroke styles aligned with screenshot
const customStyles: StylesConfig<Option, false, GroupBase<Option>> = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: state.isDisabled ? "#f9fafb" : "#ffffff", // gray-50 when disabled
    borderColor: state.isDisabled
      ? "#d1d5db" // gray-300 when disabled
      : state.isFocused
      ? "#339274" // Primary green when focused (matching EquipmentForm)
      : state.hasValue
      ? "#339274" // Primary green when selected
      : "#d1d5db", // gray-300 neutral stroke
    borderWidth: "1px",
    borderRadius: "0.75rem", // rounded-xl to match input fields
    boxShadow: state.isFocused
      ? "0 1px 2px rgba(0,0,0,0.08)"
      : "0 1px 2px rgba(0,0,0,0.05)", // subtle shadow always
    minHeight: "3rem", // h-12 to match input height
    fontSize: "1rem", // text-base to match input text size
    fontWeight: "500",
    transition: "all 0.2s ease",
    cursor: state.isDisabled ? "not-allowed" : "pointer",
    opacity: state.isDisabled ? 0.6 : 1,
    "&:hover": {
      borderColor: state.isDisabled ? "#d1d5db" : "#339274", // Primary green on hover
    },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb", // gray-200
    borderRadius: "0.75rem", // rounded-xl to match input fields
    boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
    marginTop: "6px",
    zIndex: 99999,
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 99999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#339274" // Primary green when selected
      : state.isFocused
      ? "rgba(51, 146, 116, 0.08)" // light primary green focus
      : "transparent",
    color: state.isSelected ? "#ffffff" : "#111827", // text-gray-900
    fontWeight: state.isSelected ? "600" : "500",
    padding: "0.65rem 1rem",
    borderRadius: "0.375rem", // rounded-md
    transition: "all 0.15s ease-in-out",
    "&:hover": {
      backgroundColor: state.isSelected
        ? "#29795f" // Darker primary green hover
        : "rgba(51, 146, 116, 0.12)",
    },
  }),
  singleValue: (provided, state) => ({
    ...provided,
    color: state.isDisabled ? "#6b7280" : "#111827", // gray-500 when disabled, text-gray-900 otherwise
    fontWeight: "500",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#6b7280", // text-gray-500 - slightly darker grey
    fontWeight: "400",
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: state.isFocused ? "#339274" : "#6b7280", // gray-500
    "&:hover": { color: "#339274" },
  }),
  indicatorSeparator: () => ({ display: "none" }),
};

const Select = forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      options,
      placeholder = "Select an option",
      onChange,
      className = "",
      value,
      isLoading = false,
      isDisabled = false,
      error,
      isCreatable = false,
      onCreateOption,
      menuPortalTarget,
    },
    ref
  ) => {
    const selectedOption: Option | null = (() => {
      if (!value) return null;
      if (typeof value !== "string") return value as Option;
      const found = options.find((opt) => opt.value === value);
      if (found) return found;
      return isCreatable ? { value, label: value } : null;
    })();

    const handleChange = (option: SingleValue<Option>) => {
      onChange(option ?? null);
    };

    const CommonProps = {
      options,
      placeholder,
      value: selectedOption || null,
      onChange: handleChange,
      styles: customStyles,
      className,
      isSearchable: true,
      isClearable: true,
      isLoading,
      isDisabled,
      menuPortalTarget,
    } as const;

    return (
      <div ref={ref} className="w-full">
        {isCreatable ? (
          <CreatableSelect<Option, false, GroupBase<Option>>
            {...CommonProps}
            onCreateOption={onCreateOption}
            formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
          />
        ) : (
          <SelectBase<Option, false, GroupBase<Option>> {...CommonProps} />
        )}
        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}
      </div>
    );
  }
);

export default Select;
